// Copied from apps/admin/src/features/map-view/components/AutoFitBounds.tsx (PRD #875).
import { useMap } from "react-leaflet";
import { MapObject } from "../ui/map-view";
import { useEffect, useRef } from "react";
import L from "leaflet";
import type { LatLngExpression } from "leaflet";

const toLatLng = (p: LatLngExpression): [number, number] | null => {
  if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
  const o = p as { lat?: number; lng?: number };
  return o.lat == null || o.lng == null ? null : [o.lat, o.lng];
};

// Automatically fit bounds to show all markers
export const AutoFitBounds: React.FC<{
  objects?: MapObject[];
  trucks?: MapObject[];
  depots?: MapObject[];
  /** Drawn route segments, so the fit contains the track and not just the pins. */
  routePaths?: LatLngExpression[][];
  infoId: string | null;
  /** Pixels a bar over the map's bottom edge hides, e.g. the playback player. */
  bottomInset?: number;
  /** A new value fits the same items again, e.g. after zooming in on one of them. */
  refitKey?: string | number;
  /** Once no route is drawn, go back to the view from before the first route fit. */
  restoreViewAfterRoute?: boolean;
}> = ({
  objects,
  trucks,
  depots,
  routePaths,
  infoId,
  bottomInset = 0,
  refitKey,
  restoreViewAfterRoute = false,
}) => {
  const map = useMap();
  const hasInitialized = useRef(false);
  const fittedTo = useRef<string | null>(null);
  const pendingFit = useRef<(() => void) | null>(null);
  const viewBeforeRoute = useRef<{ center: L.LatLng; zoom: number; ids: string[] } | null>(null);
  const restoredIds = useRef<Set<string> | null>(null);
  const fittedIds = useRef<string[]>([]);

  useEffect(() => {
    const routePoints = (routePaths || [])
      .flat()
      .map(toLatLng)
      .filter((p): p is [number, number] => p !== null);

    // A drawn route is the subject of the map, so it is fitted even though
    // selecting it sets infoId.
    if (infoId && routePoints.length === 0) return;

    const markers = [...(objects || []), ...(trucks || []), ...(depots || [])];
    const points: [number, number][] = [
      ...markers.map((m) => [m.lat, m.lon] as [number, number]),
      ...routePoints,
    ].filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));

    if (points.length === 0) return;
    if (points.length === 1 && routePoints.length === 0) return;

    // Keyed on which items are shown, not where they are: position polling
    // rebuilds these arrays every few seconds, and refitting on that dragged
    // the view back out from under anyone panning or zooming. The truck moves
    // with playback, so its coordinates must stay out of the key too. The
    // route counts by its start, not its length or end: a live route's track
    // grows with every refetch, and refitting on that does the same.
    const start = routePoints.length ? routePoints[0].join(",") : "";
    const key = [markers.map((m) => m.id).join(","), start, refitKey].join("|");
    if (fittedTo.current === key) return;

    const ids = markers.map((m) => String(m.id));
    if (restoreViewAfterRoute && routePoints.length > 0 && !viewBeforeRoute.current) {
      viewBeforeRoute.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
        ids: fittedIds.current,
      };
    } else if (routePoints.length === 0 && viewBeforeRoute.current) {
      const { center, zoom, ids: earlierIds } = viewBeforeRoute.current;
      viewBeforeRoute.current = null;
      restoredIds.current = new Set(earlierIds);
      fittedTo.current = key;
      map.setView(center, zoom);
      return;
    }
    // Hidden trucks come back over several renders, and the fleet may have changed while
    // the route was open; only a mostly different set (another company) refits.
    if (restoredIds.current && routePoints.length === 0) {
      const earlier = restoredIds.current;
      const shared = ids.filter((id) => earlier.has(id)).length;
      if (shared * 2 >= Math.max(ids.length, earlier.size)) {
        fittedTo.current = key;
        return;
      }
    }
    restoredIds.current = null;
    fittedTo.current = key;
    fittedIds.current = ids;

    const fit = () => {
      pendingFit.current = null;
      map.fitBounds(L.latLngBounds(points), {
        paddingTopLeft: [24, 24],
        paddingBottomRight: [24, 24 + bottomInset],
        animate: hasInitialized.current,
        maxZoom: 16,
      });
      hasInitialized.current = true;
    };
    // Leaflet drops a zoom asked for mid zoom-animation, and a route's pieces
    // arrive in quick succession: its final fit was lost to the previous one.
    if (pendingFit.current) map.off("zoomend", pendingFit.current);
    if ((map as unknown as { _animatingZoom?: boolean })._animatingZoom) {
      pendingFit.current = fit;
      map.once("zoomend", fit);
    } else {
      fit();
    }
  }, [
    objects,
    trucks,
    depots,
    routePaths,
    infoId,
    bottomInset,
    refitKey,
    restoreViewAfterRoute,
    map,
  ]);

  useEffect(
    () => () => {
      if (pendingFit.current) map.off("zoomend", pendingFit.current);
    },
    [map],
  );

  return null;
};
